import React from 'react';
import useMultiCardForm from 'Modules/creditDebit/hooks/useMultiCardForm';
import { SuccessButton, OutlineButton, LinkButton } from 'Core/components/Button/Button';
import { useFieldArray } from 'react-hook-form';
import { getDecimals, getPreferredPaymentMethod } from 'Core/utils/utils';
import CardSelector from 'Core/components/CardSelector';
import AmountSplitter from 'Modules/creditDebit/components/AmountSplitter';
import StyledFormGroup from 'Core/components/FormElements/StyledFormGroup';
import InputField from 'Core/components/FormElements/InputField';
import { Trans, useTranslation } from 'react-i18next';
import TermsInput from 'Core/components/FormElements/TermsInput';
import { formatCurrency } from 'Core/utils/numberFormat';
import Card from 'Core/components/FormElements/Card';
import { AlertError } from 'Core/components/Alert';

function LockIcon({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm2-2v2h6V7a3 3 0 00-6 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SecurePaymentNote() {
  const { t } = useTranslation();
  return (
    <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 md:mt-0 mt-3">
      <LockIcon />
      <span>{t('payment.securePaymentStripe', 'Secure payment method powered by Stripe')}</span>
    </p>
  );
}

function CheckoutButton({ disabled = false, amount }) {
  const { t } = useTranslation();
  const label = amount
    ? `Complete Payment - ${formatCurrency(Number(amount), {
        currency: 'USD',
        withDecimals: true,
      })}`
    : null;
  return (
    <SuccessButton
      block={true}
      type="submit"
      data-testid="checkout"
      size="tall"
      className="!shadow-md hover:!shadow-lg transition-shadow"
      disabled={disabled}
    >
      {label ?? t('common.checkout')}
    </SuccessButton>
  );
}

const PaymentCardForm = ({ paymentMethods, onSubmit, cardErrors, totalAmount }) => {
  const { t } = useTranslation();

  //We do this to manage decimals separately in the onSubmit handler
  const totalAmountInteger = Math.floor(totalAmount);

  const { paymentMethodId } = getPreferredPaymentMethod(paymentMethods);
  const defaultCardValues = paymentMethodId
    ? { paymentMethodId, amount: totalAmountInteger.toString() }
    : {
        firstName: '',
        lastName: '',
        postalCode: '',
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
        amount: totalAmountInteger.toString(),
      };
  const form = useMultiCardForm(defaultCardValues, { decimals: getDecimals(totalAmount, 6) });
  const { register, handleSubmit, control, setValue, watch } = form;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'cards',
  });

  const cards = watch('cards') ?? [];
  const termsAccepted = watch('terms');

  const [isCheckoutDisabled, setIsCheckoutDisabled] = React.useState(true);
  React.useEffect(() => {
    const isCardComplete = (card) =>
      card?.paymentMethodId ||
      (card?.firstName?.trim() &&
        card?.lastName?.trim() &&
        card?.postalCode?.trim() &&
        card?.cardNumber &&
        card?.cardExpiry &&
        card?.cardCvc);
    const allCardsComplete = cards.length > 0 && cards.every(isCardComplete);
    setIsCheckoutDisabled(!termsAccepted || !allCardsComplete);
  }, [termsAccepted, cards]);

  const remainingToCharge =
    totalAmountInteger -
    form
      .watch('cards')
      .reduce((acc, curr) => acc + (curr.amount ? parseInt(curr.amount, 10) : 0), 0);

  //This is necessary to update the form state when you remove a card.
  //You cannot call 2 actions together and need to wait for a rerender
  React.useEffect(() => {
    if (fields.length === 1) setValue('cards[0].amount', totalAmountInteger.toString());
  }, [fields.length, setValue, totalAmountInteger]);

  //Set remaining to form
  React.useEffect(() => {
    setValue('remaining', remainingToCharge, { shouldValidate: true });
  }, [remainingToCharge, setValue]);

  return (
    <form name="multiCardForm" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Total Amount Header - Modern gradient card */}
      <div className="bg-gradient-to-r from-primary/5 via-blue-50 to-primary/5 rounded-xl border border-primary/20 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">
              {t('payment.totalAmountLabel')}
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">
              <Trans
                i18nKey={`{{amount}}`}
                values={{ amount: formatCurrency(totalAmount, { withDecimals: true }) }}
              />
            </p>
          </div>
          <div className="hidden sm:flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
            <svg
              className="w-7 h-7 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Card Selection Forms */}
      {fields.map(({ id, ...field }, i) => {
        const cardError = cardErrors?.[i]?.message;
        return (
          <div key={id} className="relative">
            {/* Card number badge for multiple cards */}
            {fields.length > 1 && (
              <div className="absolute -top-3 left-6 z-10">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-sm font-semibold shadow-md">
                  {i + 1}
                </span>
              </div>
            )}
            <StyledFormGroup error={cardError}>
              <CardSelector
                update={update}
                cardError={cardError}
                defaultValues={field}
                namePrefix={`cards`}
                cardCount={fields.length}
                index={i}
                form={form}
                paymentMethods={paymentMethods}
                onRemove={() => remove(i)}
              >
                <AmountSplitter
                  decimals={form.getValues('decimals')}
                  totalAmount={totalAmountInteger}
                  form={form}
                  index={i}
                  cardCount={fields.length}
                />
                <input type="hidden" {...register('decimals')} />
              </CardSelector>
              {(fields.length === 1 || i === fields.length - 1) && (
                <div className="md:hidden flex mt-6 justify-center">
                  <hr></hr>
                  <LinkButton
                    data-testid="add-card"
                    onClick={() => {
                      append({
                        paymentMethodId: '',
                        firstName: '',
                        lastName: '',
                        postalCode: '',
                        cardNumber: '',
                        cardExpiry: '',
                        cardCvc: '',
                        amount: '',
                      });
                    }}
                    className="!flex !items-center !gap-2"
                  >
                    {fields.length === 1
                      ? t('common.splitAmountIntoMultipleCards')
                      : t('common.addCard')}
                  </LinkButton>
                </div>
              )}
            </StyledFormGroup>
          </div>
        );
      })}

      {/* Remaining Amount Error */}
      {form.formState?.errors?.remaining && (
        <AlertError>
          <Trans
            i18nKey={`payment.remainingAmountToCharge`}
            values={{ amount: formatCurrency(remainingToCharge, { withDecimals: false }) }}
          />
        </AlertError>
      )}

      {/* Add Card Section - matches amount to pay styling */}
      <div className="md:block hidden bg-gradient-to-r from-primary/5 via-blue-50 to-primary/5 rounded-xl border border-primary/20 py-6 md:px-8 px-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-base font-regular text-gray-900 mb-1">{t('common.splitAmount')}</p>
            <p className="text-sm font-regular text-gray-600 text-justify mb-0">
              {t('common.addCardControlLabel')}
            </p>
          </div>
          <div className="flex items-end justify-end gap-3 flex-grow">
            <OutlineButton
              data-testid="add-card"
              onClick={() => {
                append({
                  paymentMethodId: '',
                  firstName: '',
                  lastName: '',
                  postalCode: '',
                  cardNumber: '',
                  cardExpiry: '',
                  cardCvc: '',
                  amount: '',
                });
              }}
              className="!flex !items-center !gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('common.addCard')}
            </OutlineButton>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <Card className="!shadow-sm">
        <TermsInput
          name="terms"
          register={register}
          errors={form.formState.errors}
          label={t('common.termsAndConditions')}
        >
          <p className="mb-0 text-gray-600">{t('terms.card.charge')}</p>
        </TermsInput>
      </Card>

      <div className="flex flex-wrap justify-between items-end">
        <div className="md:pr-4 pr-0 w-full flex-col-12 md:!flex-col-6">
          <Card className=" w-full ">
            <div className="md:mt-0 flex-col-12 md:!flex-col-6 md:pt-2">
              <InputField
                register={register}
                label={t('common.receiptEmailOptional')}
                name={`email`}
                error={form.formState.errors?.email?.message}
              />
            </div>
          </Card>
        </div>

        {/* Desktop only: same checkout bar UI, part of flex layout */}
        <div className="w-full flex-col-12 md:!flex-col-6 hidden md:block">
          <Card className="w-full">
            <SecurePaymentNote />
            <CheckoutButton disabled={isCheckoutDisabled} amount={totalAmount} />
          </Card>
        </div>
      </div>

      {/* Mobile only: same checkout bar UI, sticky at bottom */}
      <div className="md:hidden sticky bottom-0 z-10 -mx-4 -mb-4 mt-4">
        <div className="px-4 pb-4 pt-4 bg-white/70 backdrop-blur-sm border-t border-gray-200/80 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
          <CheckoutButton disabled={isCheckoutDisabled} amount={totalAmount} />
          <SecurePaymentNote />
        </div>
      </div>
    </form>
  );
};

export default PaymentCardForm;
